class FreeShippingProgress extends HTMLElement {
  constructor() {
    super();
    this.refreshTimer = null;
    this.refreshSequence = 0;
    this.observer = null;
    this.onCartUpdated = this.scheduleRefresh.bind(this);
  }

  connectedCallback() {
    this.threshold = Number(this.dataset.threshold || 0);
    this.messageText = this.querySelector('[data-free-shipping-message-text]');
    this.track = this.querySelector('[data-free-shipping-track]');
    this.bar = this.querySelector('[data-free-shipping-bar]');
    this.container = this.closest('.header__free-shipping-progress');
    this.specialHandles = new Set(
      String(this.dataset.specialHandles || '')
        .split('|')
        .map((handle) => handle.trim())
        .filter(Boolean)
    );

    if (!this.threshold || !this.messageText || !this.track || !this.bar || !this.container) return;

    if (this.isTradeCustomer()) {
      this.setHidden(true);
      return;
    }

    this.update(Number(this.dataset.cartTotal || 0));
    this.observeCartChanges();
    document.addEventListener('tfb:cart-updated', this.onCartUpdated);
    document.addEventListener('shopify:cart:lines-update', this.onCartUpdated);
    this.refresh();
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    if (this.refreshTimer) window.clearTimeout(this.refreshTimer);
    document.removeEventListener('tfb:cart-updated', this.onCartUpdated);
    document.removeEventListener('shopify:cart:lines-update', this.onCartUpdated);
  }

  isTradeCustomer() {
    return Boolean(window.isTradeCustomer || window.TFBTradeDeliveryConfig?.isB2BCustomer);
  }

  observeCartChanges() {
    const targets = [
      document.getElementById('cart-icon-bubble'),
      document.getElementById('main-cart-items'),
      document.getElementById('main-cart-footer'),
    ].filter(Boolean);

    if (targets.length === 0) return;

    this.observer = new MutationObserver(() => this.scheduleRefresh());
    targets.forEach((target) => {
      this.observer.observe(target, { childList: true, subtree: true, characterData: true });
    });
  }

  scheduleRefresh() {
    if (this.refreshTimer) window.clearTimeout(this.refreshTimer);
    this.refreshTimer = window.setTimeout(() => this.refresh(), 120);
  }

  async refresh() {
    if (this.isTradeCustomer()) {
      this.setHidden(true);
      return;
    }

    const sequence = ++this.refreshSequence;
    const root = window.Shopify?.routes?.root || '/';

    try {
      const response = await fetch(`${root}cart.js`, {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });

      if (!response.ok) throw new Error('Unable to refresh the cart total.');

      const cart = await response.json();
      if (sequence !== this.refreshSequence) return;

      let eligibleTotal = Number(cart.total_price || 0);
      let hasSpecialDelivery = false;

      cart.items.forEach((item) => {
        const sku = String(item.sku || '').trim().toUpperCase();
        const handle = String(item.handle || '').trim();
        const isTradeItem =
          sku.endsWith('-TRADE') ||
          sku === 'TFB-TRADE-DELIVERY' ||
          handle === 'trade-delivery-charge-8';
        const isSpecialItem =
          Boolean(item.selling_plan_allocation) ||
          this.specialHandles.has(handle);

        if (isTradeItem || isSpecialItem) {
          hasSpecialDelivery = true;
          return;
        }

        if (item.requires_shipping === false) {
          eligibleTotal -= Number(item.final_line_price || 0);
        }
      });

      eligibleTotal = Math.max(0, eligibleTotal);
      const noEligiblePhysicalItems = Number(cart.item_count || 0) > 0 && eligibleTotal === 0;
      this.setHidden(hasSpecialDelivery || noEligiblePhysicalItems);

      if (!hasSpecialDelivery && !noEligiblePhysicalItems) {
        this.update(eligibleTotal);
      }
    } catch (error) {
      console.warn('Free-shipping progress could not refresh.', error);
    }
  }

  setHidden(hidden) {
    this.container.hidden = hidden;
  }

  formatMoney(cents) {
    const hasPence = cents % 100 !== 0;

    return new Intl.NumberFormat(this.dataset.locale || 'en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: hasPence ? 2 : 0,
      maximumFractionDigits: 2,
    }).format(cents / 100);
  }

  update(cartTotal) {
    const safeTotal = Math.max(0, Number(cartTotal || 0));
    const amountRemaining = Math.max(0, this.threshold - safeTotal);
    const progress = Math.min(100, (safeTotal / this.threshold) * 100);

    if (safeTotal === 0) {
      this.messageText.textContent = `Free standard UK delivery when you spend ${this.formatMoney(this.threshold)}`;
    } else if (amountRemaining > 0) {
      this.messageText.textContent = `${this.formatMoney(amountRemaining)} to free standard UK delivery`;
    } else {
      this.messageText.textContent = 'Free standard UK delivery unlocked';
    }

    this.bar.style.width = `${progress}%`;
    this.track.setAttribute('aria-valuenow', String(Math.min(safeTotal, this.threshold)));
  }
}

if (!customElements.get('free-shipping-progress')) {
  customElements.define('free-shipping-progress', FreeShippingProgress);
}
